import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Tab, Tabs,
    CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, TextField, MenuItem,
} from '@mui/material';
import { Icon } from '@iconify/react';
import fileTextFill from '@iconify/icons-eva/file-text-fill';
import uploadFill from '@iconify/icons-eva/upload-fill';
import { founderSyndicationApi } from '../../utils/syndicationApi';
import SyndicationDocumentViewer from '../../components/syndication/SyndicationDocumentViewer';

const STATUS_COLORS = {
    active: 'success', qa_open: 'info', commitment_open: 'warning',
    allocation: 'secondary', completed: 'default',
};

const CATEGORY_OPTIONS = [
    { value: 'pitch_deck', label: 'Pitch Deck' },
    { value: 'financials', label: 'Financials' },
    { value: 'legal', label: 'Legal' },
    { value: 'technical', label: 'Technical' },
    { value: 'market_research', label: 'Market Research' },
    { value: 'other', label: 'Other' },
];

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function FounderDealRoom() {
    const { dealCode } = useParams();
    const [deal, setDeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(0);

    // Upload state
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadCategory, setUploadCategory] = useState('other');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState('');
    const [uploadErr, setUploadErr] = useState('');
    const fileInputRef = useRef(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        fetchDeal();
    }, [dealCode]);

    const fetchDeal = async () => {
        try {
            const res = await founderSyndicationApi.get(`founder/deals/${dealCode}/`);
            setDeal(res.data?.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load deal.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!uploadFile || !uploadTitle.trim()) {
            setUploadErr('Title and file are required.');
            return;
        }
        setUploading(true);
        setUploadErr('');
        setUploadMsg('');
        try {
            const formData = new FormData();
            formData.append('title', uploadTitle.trim());
            formData.append('category', uploadCategory);
            formData.append('file', uploadFile);
            await founderSyndicationApi.post(
                `founder/deals/${dealCode}/documents/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setUploadMsg('Document uploaded. Pending admin approval.');
            setUploadTitle('');
            setUploadCategory('other');
            setUploadFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchDeal(); // Refresh
        } catch (err) {
            setUploadErr(err.response?.data?.message || 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (doc) => {
        setDownloading(doc.id);
        try {
            const res = await founderSyndicationApi.get(
                `founder/deals/${dealCode}/documents/${doc.id}/download/`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.title || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // Fallback: open file URL directly
            const apiBase = `${window.location.protocol}//${window.location.hostname}:8000`;
            const fileUrl = doc.file?.startsWith('/') ? doc.file : `/media/${doc.file}`;
            window.open(`${apiBase}${fileUrl}`, '_blank');
        } finally {
            setDownloading(null);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!deal) return null;

    const docs = deal.documents || [];
    const presentations = deal.presentations || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="overline" color="text.secondary">{deal.deal_code}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{deal.title || 'Untitled Deal'}</Typography>
                    <Typography variant="body1" color="text.secondary">{deal.company_name || '—'}</Typography>
                </Box>
                <Chip label={deal.status?.replace(/_/g, ' ')} color={STATUS_COLORS[deal.status] || 'default'} />
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Overview" />
                <Tab label={`Documents (${docs.length})`} />
                <Tab label="Upload Document" />
                {presentations.length > 0 && <Tab label="Presentations" />}
            </Tabs>

            {/* Overview */}
            <TabPanel value={tab} index={0}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Deal Details</Typography>
                        <Grid container spacing={2}>
                            {[
                                ['Total Raise', `${deal.currency_label || ''} ${Number(deal.total_raise || 0).toLocaleString()}`],
                                ['Co-investor Allocation', `${deal.currency_label || ''} ${Number(deal.coinvestor_allocation || 0).toLocaleString()}`],
                                ['Min Ticket', `${deal.currency_label || ''} ${Number(deal.min_ticket_size || 0).toLocaleString()}`],
                                ['Max Ticket', `${deal.currency_label || ''} ${Number(deal.max_ticket_size || 0).toLocaleString()}`],
                                ['Instrument', deal.instrument_type || '—'],
                                ['Commitment Deadline', deal.commitment_deadline ? new Date(deal.commitment_deadline).toLocaleDateString() : '—'],
                            ].map(([label, value]) => (
                                <Grid item xs={6} sm={4} key={label}>
                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                        {deal.description && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>{deal.description}</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Documents */}
            <TabPanel value={tab} index={1}>
                {docs.length === 0 ? (
                    <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No approved documents yet.</Typography>
                    </CardContent></Card>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Size</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {docs.map((doc) => (
                                    <TableRow key={doc.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon={fileTextFill} width={20} color="#666" />
                                                <Typography variant="body2" fontWeight={600}>{doc.title}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell><Chip label={doc.category} size="small" variant="outlined" /></TableCell>
                                        <TableCell>{doc.file_type?.toUpperCase()}</TableCell>
                                        <TableCell align="right">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : '—'}</TableCell>
                                        <TableCell align="center">
                                            <SyndicationDocumentViewer
                                                fileUrl={doc.file}
                                                fileType={doc.file_type}
                                                title={doc.title}
                                                onDownload={() => handleDownload(doc)}
                                                downloading={downloading === doc.id}
                                                iconOnly
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </TabPanel>

            {/* Upload Document */}
            <TabPanel value={tab} index={2}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Upload New Document</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Uploaded documents will be reviewed and approved by an admin before becoming visible to investors.
                        </Typography>

                        {uploadMsg && <Alert severity="success" sx={{ mb: 2 }}>{uploadMsg}</Alert>}
                        {uploadErr && <Alert severity="error" sx={{ mb: 2 }}>{uploadErr}</Alert>}

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Document Title" value={uploadTitle}
                                    onChange={(e) => setUploadTitle(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth select label="Category" value={uploadCategory}
                                    onChange={(e) => setUploadCategory(e.target.value)}
                                >
                                    {CATEGORY_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <input
                                    ref={fileInputRef} type="file" style={{ display: 'none' }}
                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png,.mp4"
                                />
                                <Button
                                    variant="outlined" onClick={() => fileInputRef.current?.click()}
                                    startIcon={<Icon icon={uploadFill} />}
                                >
                                    {uploadFile ? uploadFile.name : 'Choose File'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                    Max 50 MB. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, MP4
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained" onClick={handleUpload} disabled={uploading}
                                    sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                                >
                                    {uploading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Upload Document'}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Presentations */}
            {presentations.length > 0 && (
                <TabPanel value={tab} index={3}>
                    <Grid container spacing={2}>
                        {presentations.map((pres) => (
                            <Grid item xs={12} sm={6} key={pres.id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" fontWeight={700}>{pres.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {new Date(pres.scheduled_at).toLocaleString()} ({pres.timezone})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Duration: {pres.duration_minutes} min
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Chip label={pres.status} size="small"
                                                color={pres.status === 'scheduled' ? 'info' : pres.status === 'completed' ? 'success' : 'default'} />
                                        </Box>
                                        {pres.teams_meeting_url && (
                                            <Button size="small" href={pres.teams_meeting_url} target="_blank" sx={{ mt: 1 }}>
                                                Join Meeting
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </TabPanel>
            )}
        </Box>
    );
}
